import * as React from 'react';
import { JRCSkeletonSquare } from '../JRCSkeleton/JRCSkeletonSquare';
import styled from 'styled-components';
import { JRCImg, JRCImgProps } from '../JRCImg/JRCImg';
import { JRCConditionalWrapper } from '../Common/JRCConditionalWrapper';

/**
 * Props type for JRCAvatar
 * @see JRCImgProps
 * /!\ It is not recommended to use the url property as it will result in overriding
 * the default image size
 *
 * @property size, multiple of 8px - The avatar image is downloaded
 *         with a size twice of the circle/square container in order to make it clear
 *         in retina like screens. default: xs
 * @property variant, optional 'circle' | 'square' - default: circle
 * @property focusable - whether the element is focusable. Independent of onClick property
 * @property subAvatar - represent a miniature avatar on the bottom right corner
 * @property className - className passed to the div wrapper
 * @property cssColor - background-color
 * @property iconWhite - Toggle default icon color white/black
 * @property from - image base url (imagestatic | imagecache)
 */
export type JRCAvatarProps = Omit<JRCImgProps, 'size' | 'width' | 'height' | 'alt'> & {
    uri?: string;
    size?: number;
    variant?: 'circle' | 'square';
    focusable?: boolean;
    subAvatar?: Omit<JRCImgProps, 'size' | 'width' | 'height'>;
    className?: string;
    cssColor?: string;
    iconWhite?: boolean;
    alt?: string;
    testId?: string;
    onLoad?: () => void;
};

const BASE_MULTIPLE = 8;

type ExtendedImgProps = {
    variant?: 'circle' | 'square';
    focusable?: boolean;
    width: number;
    cssColor: string;
};

/**
 * border is calculated to be 2px when the avatar is 24px in width and 4px when the avatar is 80px
 */
const Avatar = styled(JRCImg).attrs<ExtendedImgProps>((props) => ({
    ...props,
    border: props.width / 28 + 8 / 7,
}))<ExtendedImgProps & { border: number; isLoading: boolean }>`
    opacity: ${(props) => (props.isLoading ? 0 : 1)};
    cursor: ${(props) => (props.focusable ? 'pointer' : 'default')};
    border-radius: ${(props) => (props.variant === 'square' ? '20%' : '50%')};
    background-color: ${(props) => props.cssColor || 'transparent'};
    vertical-align: middle;

    & + img {
        border: ${(props) => props.border}px solid white;
        box-sizing: content-box;
        margin-left: -${(props) => props.width / 2 + props.border}px;
        margin-bottom: -${(props) => props.width / 2 + props.border}px;
    }
`;

const SkeletonSquare = styled(JRCSkeletonSquare)`
    position: absolute;
`;

const SubContainer = styled.div<{ isLoading: boolean }>`
    display: inline;
    position: relative;
`;

const SubSkeletonSquare = styled(JRCSkeletonSquare)<{ imgSize: number }>`
    position: absolute;
    bottom: ${(props) => props.imgSize / 4}px;
    left: ${(props) => props.imgSize / 2}px;
`;

export const JRCAvatar = (props: JRCAvatarProps) => {
    const { subAvatar, className, size, from, format, alt, iconWhite, testId, ...otherProps } = props;
    const imgSize = (size || 3) * BASE_MULTIPLE;
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    function endLoading() {
        setIsLoading(false);
    }

    function onError() {
        endLoading();
        if (props.onError) props.onError();
    }

    return (
        <JRCConditionalWrapper
            condition={!!props.subAvatar}
            wrapper={(children) => <div className={className}>{children}</div>}>
            <SubContainer data-testid={testId} isLoading={isLoading}>
                {isLoading && <SkeletonSquare width={`${imgSize.toString()}px`} borderRadius="50%" />}
                {React.createElement(Avatar, {
                    ...otherProps,
                    width: imgSize,
                    height: imgSize,
                    format: format,
                    alt: alt,
                    'aria-hidden': alt ? undefined : true,
                    from: from ?? 'imagecache/',
                    iconColor: iconWhite === false ? undefined : 'white',
                    isLoading: isLoading,
                    onError: onError,
                    onLoad: endLoading,
                })}
                {props.subAvatar && (
                    <React.Fragment>
                        {isLoading && (
                            <SubSkeletonSquare
                                imgSize={imgSize}
                                width={`${(imgSize / 2).toString()}px`}
                                borderRadius="50%"
                            />
                        )}
                        {React.createElement(Avatar, {
                            ...subAvatar,
                            from: subAvatar?.from || 'imagecache/',
                            width: imgSize / 2,
                            height: imgSize / 2,
                            iconColor: 'white',
                            isLoading: isLoading,
                        })}
                    </React.Fragment>
                )}
            </SubContainer>
        </JRCConditionalWrapper>
    );
};
